/**
 * Agent Service API client.
 * Lean service — one method per API endpoint, ~3-5 lines each.
 */
import axios, { AxiosInstance } from 'axios';
import { AGENT_API_BASE_URL, AGENT_ENDPOINTS } from '../config/agentApi';
import { acquireAccessToken, isAuthEnabled } from './authService';
import type { SchemaSearchResult, SchemaSearchRequest, TerminalContextEntry } from '../types';

// Types
export interface AgentDTO {
    agentId?: string;
    name: string;
    description?: string;
    status?: string;
    /** @deprecated Use workflow + toolConfigs instead */
    workflowDefinition?: string;
    /** Structured workflow object (nodes, edges, viewport) — no secrets */
    workflow?: Record<string, any>;
    /** Tool configurations keyed by nodeId — secrets masked with *** */
    toolConfigs?: Record<string, Record<string, any>>;
    triggerType?: string;
    cronExpression?: string;
    systemPrompt?: string;
    promptTemplateId?: string;
    createdBy?: string;
    updatedBy?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface PromptTemplateDTO {
    templateId: string;
    name: string;
    content: string;
    contentSections?: Record<string, string>;
    isDefault: boolean;
    databaseType?: string;
}

export interface AgentExecutionResult {
    executionId: string;
    requestId?: string;
    sessionId?: string;
    responseType?: 'PLAINTEXT' | 'MARKDOWN' | 'JSON';
    stream?: boolean;
    response: string;
    status: string;
    executionDurationMs: number;
    toolCallCount: number;
    errorMessage?: string;
    locale?: string;
    timezone?: string;
    outputLanguage?: string;
}

export interface ExecuteAgentRequestOptions {
    requestId?: string;
    responseType?: 'PLAINTEXT' | 'MARKDOWN' | 'JSON';
    stream?: boolean;
    locale?: string;
    timezone?: string;
    outputLanguage?: string;
    metadata?: Record<string, any>;
    onChunk?: (chunk: string) => void;
}

export interface ToolCallLogResponse {
    logId: string;
    agentId: string;
    toolName: string;
    inputData: string;
    outputData: string;
    status: 'SUCCESS' | 'ERROR' | 'GUARDRAIL_BLOCKED' | 'MODERATION_BLOCKED' | 'TOOL_ERROR';
    durationMs: number;
    errorMessage?: string;
    channel: string;
    userId: string;
    executedAt: string;
}

export interface PagedToolCallLogResponse {
    content: ToolCallLogResponse[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

export interface QueryBuilderResponse {
    sqlQuery: string;
    explanation: string;
    tablesUsed: string[];
    assumptions: string[];
    validationResult: {
        isValid: boolean;
        message: string;
    };
    subAgentUsed: string;
}

export interface AiUsageStatsResponse {
    userId: string;
    totalRequests: number;
    totalPromptTokens: number;
    totalCompletionTokens: number;
    totalTokens: number;
    updatedAt?: string;
}

export interface AiUsageWindowStatsResponse {
    userId: string;
    windowStartEpochMs: number;
    windowResetEpochMs: number;
    windowSeconds: number;
    currentRequests: number;
    currentTokens: number;
    maxRequestsPerWindow: number;
    maxTokensPerWindow: number;
    remainingRequests: number;
    remainingTokens: number;
    quotaHealthPercent?: number;
}

export interface AiUsageAdminActionResponse {
    userId: string;
    message: string;
    cacheEvicted?: boolean;
    windowReset?: boolean;
}

export interface AiUsageUsersListResponse {
    count: number;
    users: AiUsageStatsResponse[];
}

export interface AiUsageQuotaSettings {
    limitsEnabled: boolean;
    maxRequestsPerWindow: number;
    maxTokensPerWindow: number;
    windowSeconds: number;
    message?: string;
}

export interface ConversationSessionEntry {
    key: string;
    userId: string;
    agentId: string;
    sessionId: string;
    messageCount: number;
    ttlSeconds: number;
}

export interface ConversationSessionsListResponse {
    count: number;
    sessions: ConversationSessionEntry[];
}

export interface ConversationSessionDetailResponse {
    userId: string;
    agentId: string;
    sessionId: string;
    messageCount: number;
    messages: Array<{ role: string; content: string }>;
}

export interface ConversationSettings {
    ttlSeconds: number;
    maxTurns: number;
}

// Private Axios instance for agent service
const agentClient: AxiosInstance = axios.create({
    baseURL: AGENT_API_BASE_URL,
    headers: { 'Content-Type': 'application/json' },
    timeout: 30000,
});

// Auth interceptor (reuse same token strategy + robust header handling for Axios 1.x)
agentClient.interceptors.request.use(async (config) => {
    const attachHeader = (key: string, value: string) => {
        if (!config.headers) config.headers = {} as any;
        if (typeof (config.headers as any).set === 'function') {
            (config.headers as any).set(key, value);
        } else {
            (config.headers as any)[key] = value;
        }
    };

    if (isAuthEnabled()) {
        const token = await acquireAccessToken();
        if (token) attachHeader('Authorization', `Bearer ${token}`);
    } else {
        const token = localStorage.getItem('authToken');
        if (token) attachHeader('Authorization', `Bearer ${token}`);
    }

    const timezone = Intl?.DateTimeFormat?.().resolvedOptions().timeZone;
    if (timezone) attachHeader('X-User-Timezone', timezone);

    return config;
});

// Response interceptor (handle 401 Unauthorized / token expiration)
agentClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const status = error.response?.status;
        if (status === 401) {
            localStorage.removeItem('authToken');
            if (isAuthEnabled()) {
                if (!error.config._retried) {
                    error.config._retried = true;
                    const { acquireAccessToken: getToken, requireReauthentication } = await import('./authService');
                    const freshToken = await getToken(true, false);
                    if (freshToken) {
                        error.config.headers['Authorization'] = `Bearer ${freshToken}`;
                        return agentClient.request(error.config);
                    }
                    await requireReauthentication();
                }
            }
        }
        return Promise.reject(error);
    }
);

async function buildAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};

    if (isAuthEnabled()) {
        const token = await acquireAccessToken();
        if (token) headers.Authorization = `Bearer ${token}`;
    } else {
        const token = localStorage.getItem('authToken');
        if (token) headers.Authorization = `Bearer ${token}`;
    }

    const timezone = Intl?.DateTimeFormat?.().resolvedOptions().timeZone;
    if (timezone) headers['X-User-Timezone'] = timezone;

    return headers;
}

async function readSseResponse(
    response: Response,
    onChunk?: (chunk: string) => void
): Promise<string> {
    if (!response.body) {
        return '';
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let responseText = '';

    const flushEvent = (rawEvent: string) => {
        const lines = rawEvent.split(/\r?\n/);
        let eventName = 'message';
        const dataLines: string[] = [];

        for (const line of lines) {
            if (line.startsWith('event:')) {
                eventName = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trimStart());
            }
        }

        const data = dataLines.join('\n');
        if (!data) {
            return;
        }

        if (eventName === 'response' || eventName === 'message') {
            responseText += data;
            onChunk?.(data);
        }
    };

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let separatorIndex = buffer.indexOf('\n\n');
        while (separatorIndex !== -1) {
            const rawEvent = buffer.slice(0, separatorIndex).trimEnd();
            buffer = buffer.slice(separatorIndex + 2);
            if (rawEvent) flushEvent(rawEvent);
            separatorIndex = buffer.indexOf('\n\n');
        }
    }

    const finalEvent = buffer.trim();
    if (finalEvent) {
        flushEvent(finalEvent);
    }

    return responseText;
}

// ── CRUD ─────────────────────────────────────────────
export const agentService = {
    async list(status?: string): Promise<AgentDTO[]> {
        const { data } = await agentClient.get(AGENT_ENDPOINTS.agents, { params: { status } });
        return data;
    },

    async get(agentId: string): Promise<AgentDTO> {
        const { data } = await agentClient.get(AGENT_ENDPOINTS.agentById(agentId));
        return data;
    },

    async create(agent: Partial<AgentDTO>): Promise<AgentDTO> {
        const { data } = await agentClient.post(AGENT_ENDPOINTS.agents, agent);
        return data;
    },

    async update(agentId: string, agent: Partial<AgentDTO>): Promise<AgentDTO> {
        const { data } = await agentClient.put(AGENT_ENDPOINTS.agentById(agentId), agent);
        return data;
    },

    async remove(agentId: string): Promise<void> {
        await agentClient.delete(AGENT_ENDPOINTS.agentById(agentId));
    },

    // ── Lifecycle ───────────────────────────────────────
    async deploy(agentId: string): Promise<AgentDTO> {
        const { data } = await agentClient.post(AGENT_ENDPOINTS.deploy(agentId));
        return data;
    },

    async test(agentId: string): Promise<AgentDTO> {
        const { data } = await agentClient.post(AGENT_ENDPOINTS.test(agentId));
        return data;
    },

    async pause(agentId: string): Promise<AgentDTO> {
        const { data } = await agentClient.post(AGENT_ENDPOINTS.pause(agentId));
        return data;
    },

    // ── Prompt Templates ────────────────────────────────
    async getPromptTemplates(databaseType?: string): Promise<PromptTemplateDTO[]> {
        const { data } = await agentClient.get(AGENT_ENDPOINTS.promptTemplates, { params: { databaseType } });
        return data;
    },

    async updatePromptTemplate(
        templateId: string,
        body: { name?: string; content?: string; contentSections?: Record<string, string>; databaseType?: string }
    ): Promise<PromptTemplateDTO> {
        const { data } = await agentClient.put(`${AGENT_ENDPOINTS.promptTemplates}/${templateId}`, body);
        return data;
    },

    // ── Execution ───────────────────────────────────────
    async execute(
        agentId: string,
        message: string,
        channel = 'web_ui',
        conversationHistory?: Array<{ role: string; content: string }>,
        sessionId?: string,
        modelName?: string,
        options: ExecuteAgentRequestOptions = {}
    ): Promise<AgentExecutionResult & { sessionId?: string }> {
        const requestBody = {
            message,
            channel,
            ...(conversationHistory?.length ? { conversationHistory } : {}),
            ...(sessionId ? { sessionId } : {}),
            ...(modelName ? { modelName } : {}),
            ...(options.requestId ? { requestId: options.requestId } : {}),
            ...(options.responseType ? { responseType: options.responseType } : {}),
            ...(typeof options.stream === 'boolean' ? { stream: options.stream } : {}),
            ...(options.locale ? { locale: options.locale } : {}),
            ...(options.timezone ? { timezone: options.timezone } : {}),
            ...(options.outputLanguage ? { outputLanguage: options.outputLanguage } : {}),
            ...(options.metadata ? { metadata: options.metadata } : {}),
        };

        if (options.stream) {
            const startedAt = Date.now();
            const headers = await buildAuthHeaders();
            const response = await fetch(`${AGENT_API_BASE_URL}${AGENT_ENDPOINTS.execute(agentId)}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                    ...headers,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || `Agent execution failed with status ${response.status}`);
            }

            const responseText = await readSseResponse(response, options.onChunk);
            const elapsedMs = Date.now() - startedAt;

            return {
                executionId: response.headers.get('x-execution-id') || crypto.randomUUID(),
                requestId: response.headers.get('x-request-id') || options.requestId,
                sessionId: response.headers.get('x-session-id') || sessionId,
                responseType: options.responseType,
                stream: true,
                response: responseText,
                status: 'SUCCESS',
                executionDurationMs: elapsedMs,
                toolCallCount: 0,
                locale: options.locale,
                timezone: options.timezone,
                outputLanguage: options.outputLanguage,
            };
        }

        const { data } = await agentClient.post(AGENT_ENDPOINTS.execute(agentId), requestBody);
        return data;
    },

    // ── Logs ────────────────────────────────────────────
    async getLogs(agentId: string, page = 0, size = 20): Promise<PagedToolCallLogResponse> {
        const { data } = await agentClient.get(AGENT_ENDPOINTS.logs(agentId), {
            params: { page, size }
        });
        return data;
    },

    // ── Query Builder ───────────────────────────────────
    async generateQuery(request: {
        naturalLanguageQuery: string;
        terminalId: string;
        databaseType: string;
        category?: string;
        pageContext?: Record<string, any>;
        options?: any;
        conversationHistory?: { role: string; content: string }[];
    }): Promise<QueryBuilderResponse> {
        // Use a longer timeout (120s) for agent-based generation as it does multiple tool calls
        const { data } = await agentClient.post(
            AGENT_ENDPOINTS.queryBuilderGenerate,
            request,
            { timeout: 120000 }
        );
        return data;
    },

    async getUserAiUsage(userId: string): Promise<AiUsageStatsResponse> {
        const { data } = await agentClient.get(`/internal/ai-usage/users/${encodeURIComponent(userId)}`);
        return data;
    },

    async listUserAiUsage(search = '', limit = 200): Promise<AiUsageUsersListResponse> {
        const { data } = await agentClient.get('/internal/ai-usage/users', {
            params: { search, limit },
        });
        return data;
    },

    async getUserAiUsageWindow(userId: string): Promise<AiUsageWindowStatsResponse> {
        const { data } = await agentClient.get(`/internal/ai-usage/window/users/${encodeURIComponent(userId)}`);
        return data;
    },

    async resetUserAiUsageCache(userId: string): Promise<AiUsageAdminActionResponse> {
        const { data } = await agentClient.delete(`/internal/ai-usage/cache/users/${encodeURIComponent(userId)}`);
        return data;
    },

    async resetUserAiUsageWindow(userId: string): Promise<AiUsageAdminActionResponse> {
        const { data } = await agentClient.delete(`/internal/ai-usage/window/users/${encodeURIComponent(userId)}`);
        return data;
    },

    async getAiUsageQuotaSettings(): Promise<AiUsageQuotaSettings> {
        const { data } = await agentClient.get('/internal/ai-usage/settings');
        return data;
    },

    async updateAiUsageQuotaSettings(payload: Partial<AiUsageQuotaSettings>): Promise<AiUsageQuotaSettings> {
        const { data } = await agentClient.put('/internal/ai-usage/settings', payload);
        return data;
    },

    // ── Conversation Sessions (Redis) ───────────────────
    async listConversationSessions(user = '', limit = 200): Promise<ConversationSessionsListResponse> {
        const { data } = await agentClient.get('/internal/conversation-sessions', {
            params: { user, limit },
        });
        return data;
    },

    async getConversationSession(userId: string, agentId: string, sessionId: string): Promise<ConversationSessionDetailResponse> {
        const { data } = await agentClient.get(
            `/internal/conversation-sessions/${encodeURIComponent(userId)}/${encodeURIComponent(agentId)}/${encodeURIComponent(sessionId)}`
        );
        return data;
    },

    async evictConversationSession(userId: string, agentId: string, sessionId: string): Promise<{ evicted: boolean }> {
        const { data } = await agentClient.delete(
            `/internal/conversation-sessions/${encodeURIComponent(userId)}/${encodeURIComponent(agentId)}/${encodeURIComponent(sessionId)}`
        );
        return data;
    },

    async evictUserConversationSessions(userId: string): Promise<{ sessionsEvicted: number }> {
        const { data } = await agentClient.delete(`/internal/conversation-sessions/users/${encodeURIComponent(userId)}`);
        return data;
    },

    async evictAllConversationSessions(): Promise<{ sessionsEvicted: number }> {
        const { data } = await agentClient.delete('/internal/conversation-sessions');
        return data;
    },

    // ── Conversation Settings (Redis-backed) ────────────
    async getConversationSettings(): Promise<ConversationSettings> {
        const { data } = await agentClient.get('/internal/conversation-sessions/settings');
        return data;
    },

    async updateConversationSettings(payload: Partial<ConversationSettings>): Promise<ConversationSettings> {
        const { data } = await agentClient.put('/internal/conversation-sessions/settings', payload);
        return data;
    },

    // ── Schema Field Search ────────────────────────────
    async searchSchemaFields(request: SchemaSearchRequest): Promise<SchemaSearchResult[]> {
        const { data } = await agentClient.post('/api/v1/schema/search', request);
        return data;
    },

    async getTerminalFieldContext(terminalId: string): Promise<TerminalContextEntry[]> {
        const { data } = await agentClient.get(`/api/v1/schema/terminal-context/${encodeURIComponent(terminalId)}`);
        return data;
    },

    async saveTerminalFieldContext(
        terminalId: string,
        tableName: string,
        columnName: string,
        localAliases: string[],
        notes?: string,
        fkReference?: string,
        joinCondition?: string,
        domain?: string
    ): Promise<TerminalContextEntry> {
        const { data } = await agentClient.put(
            `/api/v1/schema/terminal-context/${encodeURIComponent(terminalId)}/${encodeURIComponent(tableName)}/${encodeURIComponent(columnName)}`,
            { localAliases, notes, fkReference, joinCondition, domain }
        );
        return data;
    },

    async deleteTerminalFieldContext(terminalId: string, tableName: string, columnName: string): Promise<void> {
        await agentClient.delete(
            `/api/v1/schema/terminal-context/${encodeURIComponent(terminalId)}/${encodeURIComponent(tableName)}/${encodeURIComponent(columnName)}`
        );
    },

    async refreshSchemaIndex(): Promise<string> {
        const { data } = await agentClient.post('/api/v1/admin/schema/refresh-index');
        return data;
    },
};

export default agentService;
